import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  MapPinIcon,
  PhotoIcon,
  UserIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import {
  approveInspection,
  createFollowUpInspection,
  fetchInspection,
  rejectInspection,
  requestInspectionCorrection,
  submitInspection,
  updateInspectionStatus,
} from '../../services/treeInspectionApi';
import type { InspectionAudit, TreeInspection } from './types';

interface Props {
  inspectionId: number;
  isManager: boolean;
  onClose: () => void;
  onUpdated: () => void;
}

const inputClass = 'w-full rounded-md border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100';

const humanize = (value?: string | null) => String(value || 'Not set').replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
const formatDate = (value?: string | null) => value ? new Date(value).toLocaleString() : 'Not recorded';

const statusClass = (status: string) => {
  if (status === 'approved') return 'bg-emerald-100 text-emerald-800';
  if (status === 'rejected' || status === 'overdue') return 'bg-red-100 text-red-800';
  if (status === 'correction_required') return 'bg-amber-100 text-amber-800';
  if (status === 'submitted' || status === 'resubmitted') return 'bg-blue-100 text-blue-800';
  return 'bg-slate-100 text-slate-700';
};

const InspectionReviewDrawer: React.FC<Props> = ({ inspectionId, isManager, onClose, onUpdated }) => {
  const navigate = useNavigate();
  const [inspection, setInspection] = useState<TreeInspection | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [findings, setFindings] = useState('');
  const [recommendations, setRecommendations] = useState('');
  const [healthStatus, setHealthStatus] = useState('');
  const [severity, setSeverity] = useState(1);
  const [reviewDecision, setReviewDecision] = useState<'approve' | 'correction' | 'reject' | ''>('');
  const [reviewComment, setReviewComment] = useState('');
  const [followUpDate, setFollowUpDate] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const value = await fetchInspection(inspectionId);
      setInspection(value);
      setFindings(value.findings || '');
      setRecommendations(value.recommendations || '');
      setHealthStatus(value.health_status || '');
      setSeverity(Number(value.severity_score || 1));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Unable to load inspection details.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [inspectionId]);

  const audit = useMemo<InspectionAudit[]>(() => inspection?.audit_trail || inspection?.audit || [], [inspection]);
  const canCapture = !isManager && ['in_progress', 'correction_required'].includes(inspection?.status || '');
  const canReview = isManager && ['submitted', 'resubmitted'].includes(inspection?.status || '');

  const perform = async (action: () => Promise<unknown>) => {
    setSaving(true);
    setError('');
    try {
      await action();
      await load();
      onUpdated();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Unable to update this inspection.');
    } finally {
      setSaving(false);
    }
  };

  const submitResults = () => {
    if (!findings.trim() || !healthStatus) {
      setError('Health status and inspection findings are required before submission.');
      return;
    }
    perform(() => submitInspection(inspectionId, {
      findings: findings.trim(),
      recommendations: recommendations.trim() || null,
      health_status: healthStatus,
      severity_score: severity,
    }));
  };

  const completeReview = () => {
    if (!reviewDecision || !reviewComment.trim()) {
      setError('Select a decision and add a manager review comment.');
      return;
    }
    if (reviewDecision === 'approve') perform(() => approveInspection(inspectionId, reviewComment.trim()));
    if (reviewDecision === 'correction') perform(() => requestInspectionCorrection(inspectionId, reviewComment.trim()));
    if (reviewDecision === 'reject') perform(() => rejectInspection(inspectionId, reviewComment.trim()));
  };

  const createFollowUp = () => {
    if (!followUpDate) {
      setError('Select a due date for the follow-up inspection.');
      return;
    }
    perform(() => createFollowUpInspection(inspectionId, {
      assigned_to: inspection?.assigned_to,
      due_date: followUpDate,
      notes: recommendations || reviewComment || `Follow-up for ${inspection?.inspection_code || `inspection #${inspectionId}`}`,
    }));
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-950/45" role="dialog" aria-modal="true">
      <button className="absolute inset-0 cursor-default" onClick={onClose} aria-label="Close inspection" />
      <aside className="relative flex h-full w-full max-w-3xl flex-col bg-white shadow-2xl">
        <header className="flex items-start justify-between border-b border-slate-200 px-6 py-5">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-emerald-700">Inspection record</p>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <h2 className="text-xl font-bold text-slate-950">{inspection?.inspection_code || `Inspection #${inspectionId}`}</h2>
              {inspection && <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${statusClass(inspection.status)}`}>{humanize(inspection.status)}</span>}
            </div>
          </div>
          <button onClick={onClose} className="rounded-md p-2 text-slate-500 hover:bg-slate-100" aria-label="Close"><XMarkIcon className="h-5 w-5" /></button>
        </header>

        <div className="flex-1 overflow-y-auto px-6 py-6">
          {loading && <div className="flex min-h-64 items-center justify-center gap-2 text-sm text-slate-500"><ArrowPathIcon className="h-5 w-5 animate-spin" />Loading inspection record…</div>}
          {!loading && error && !inspection && <div className="border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700">{error}</div>}
          {inspection && (
            <div className="space-y-7">
              <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {[
                  ['Field', inspection.field_name || 'Not linked', MapPinIcon],
                  ['Tree', inspection.tree_code || 'Whole field', CheckCircleIcon],
                  ['Farmer', inspection.farmer_name || 'Not assigned', UserIcon],
                  ['Agronomist', inspection.assigned_to_name || 'Not assigned', UserIcon],
                ].map(([label, value, Icon]: any) => (
                  <div key={label} className="border-b border-slate-200 pb-3">
                    <Icon className="mb-2 h-5 w-5 text-emerald-700" />
                    <p className="text-xs font-bold uppercase text-slate-400">{label}</p>
                    <p className="mt-1 text-sm font-semibold text-slate-900">{value}</p>
                  </div>
                ))}
              </section>

              <section className="border-y border-slate-200 py-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-slate-600">
                    <span><strong className="text-slate-900">Type:</strong> {humanize(inspection.inspection_type)}</span>
                    <span><strong className="text-slate-900">Priority:</strong> {humanize(inspection.priority)}</span>
                    <span><strong className="text-slate-900">Due:</strong> {inspection.due_date ? new Date(inspection.due_date).toLocaleDateString() : 'No date'}</span>
                  </div>
                  <button onClick={() => navigate('/dashboard/traceability')} className="flex items-center gap-1 text-sm font-bold text-emerald-700 hover:text-emerald-900">Open in Traceability <ArrowTopRightOnSquareIcon className="h-4 w-4" /></button>
                </div>
              </section>

              {(inspection.source_summary || inspection.objective || inspection.work_instructions || inspection.safety_instructions) && (
                <section>
                  <h3 className="text-sm font-bold uppercase text-slate-500">Assignment brief</h3>
                  <div className="mt-3 grid gap-4 sm:grid-cols-2">
                    {inspection.source_summary && <div><p className="text-xs font-bold text-slate-400">SOURCE SIGNAL</p><p className="mt-1 text-sm text-slate-700">{inspection.source_summary}</p></div>}
                    {inspection.objective && <div><p className="text-xs font-bold text-slate-400">OBJECTIVE</p><p className="mt-1 text-sm text-slate-700">{inspection.objective}</p></div>}
                    {inspection.work_instructions && <div><p className="text-xs font-bold text-slate-400">WORK INSTRUCTIONS</p><p className="mt-1 text-sm text-slate-700">{inspection.work_instructions}</p></div>}
                    {inspection.safety_instructions && <div><p className="text-xs font-bold text-slate-400">SAFETY</p><p className="mt-1 text-sm text-slate-700">{inspection.safety_instructions}</p></div>}
                  </div>
                </section>
              )}

              {!isManager && inspection.status === 'assigned' && (
                <section className="border border-emerald-200 bg-emerald-50 p-4">
                  <p className="font-bold text-emerald-900">Ready to accept</p><p className="mt-1 text-sm text-emerald-800">Accepting confirms this inspection is in your mobile and web work queue.</p>
                  <button disabled={saving} onClick={() => perform(() => updateInspectionStatus(inspectionId, 'accepted'))} className="mt-3 rounded-md bg-emerald-700 px-4 py-2 text-sm font-bold text-white disabled:opacity-50">Accept inspection</button>
                </section>
              )}
              {!isManager && inspection.status === 'accepted' && (
                <section className="border border-blue-200 bg-blue-50 p-4">
                  <p className="font-bold text-blue-900">Start field inspection</p><p className="mt-1 text-sm text-blue-800">Start when you are at the assigned field and ready to capture evidence.</p>
                  <button disabled={saving} onClick={() => perform(() => updateInspectionStatus(inspectionId, 'in_progress'))} className="mt-3 rounded-md bg-blue-700 px-4 py-2 text-sm font-bold text-white disabled:opacity-50">Start inspection</button>
                </section>
              )}

              {canCapture && (
                <section>
                  <h3 className="text-sm font-bold uppercase text-slate-500">Inspection findings</h3>
                  {inspection.status === 'correction_required' && <div className="mt-3 border-l-4 border-amber-500 bg-amber-50 p-3 text-sm text-amber-900"><strong>Correction requested:</strong> {inspection.correction_reason || inspection.review_comment}</div>}
                  <div className="mt-4 grid gap-4 sm:grid-cols-2">
                    <label className="text-sm font-semibold text-slate-700">Health status *<select className={`${inputClass} mt-1.5`} value={healthStatus} onChange={(event) => setHealthStatus(event.target.value)}><option value="">Select health</option><option value="good">Good</option><option value="fair">Fair</option><option value="poor">Poor</option><option value="critical">Critical</option></select></label>
                    <label className="text-sm font-semibold text-slate-700">Severity: {severity}<input className="mt-4 w-full accent-emerald-700" type="range" min="1" max="5" value={severity} onChange={(event) => setSeverity(Number(event.target.value))} /></label>
                  </div>
                  <label className="mt-4 block text-sm font-semibold text-slate-700">Findings *<textarea className={`${inputClass} mt-1.5`} rows={4} value={findings} onChange={(event) => setFindings(event.target.value)} /></label>
                  <label className="mt-4 block text-sm font-semibold text-slate-700">Recommendations<textarea className={`${inputClass} mt-1.5`} rows={3} value={recommendations} onChange={(event) => setRecommendations(event.target.value)} /></label>
                  <button disabled={saving} onClick={submitResults} className="mt-4 rounded-md bg-emerald-700 px-5 py-2.5 text-sm font-bold text-white disabled:opacity-50">{inspection.status === 'correction_required' ? 'Correct and resubmit' : 'Submit for manager review'}</button>
                </section>
              )}

              {!canCapture && (inspection.findings || inspection.recommendations || inspection.health_status) && (
                <section>
                  <h3 className="text-sm font-bold uppercase text-slate-500">Submitted findings</h3>
                  <div className="mt-3 grid gap-4 sm:grid-cols-3">
                    <div><p className="text-xs font-bold text-slate-400">HEALTH</p><p className="mt-1 font-semibold text-slate-900">{humanize(inspection.health_status)}</p></div>
                    <div><p className="text-xs font-bold text-slate-400">SEVERITY</p><p className="mt-1 font-semibold text-slate-900">{inspection.severity_score || 'Not set'} / 5</p></div>
                    <div><p className="text-xs font-bold text-slate-400">SUBMITTED</p><p className="mt-1 font-semibold text-slate-900">{formatDate(inspection.resubmitted_at || inspection.submitted_at)}</p></div>
                  </div>
                  <div className="mt-4 space-y-3 text-sm text-slate-700"><p><strong>Findings:</strong> {inspection.findings || 'Not supplied'}</p><p><strong>Recommendations:</strong> {inspection.recommendations || 'Not supplied'}</p></div>
                </section>
              )}

              {inspection.photo_urls && inspection.photo_urls.length > 0 && (
                <section><h3 className="flex items-center gap-2 text-sm font-bold uppercase text-slate-500"><PhotoIcon className="h-5 w-5" />Evidence</h3><div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">{inspection.photo_urls.map((url, index) => <a key={`${url}-${index}`} href={url} target="_blank" rel="noreferrer"><img src={url} alt={`Inspection evidence ${index + 1}`} className="aspect-video w-full rounded-md object-cover" /></a>)}</div></section>
              )}

              {canReview && (
                <section className="border-t border-slate-200 pt-5">
                  <h3 className="text-sm font-bold uppercase text-slate-500">Management review</h3>
                  <div className="mt-3 grid gap-3 sm:grid-cols-3">
                    {([['approve', CheckCircleIcon, 'Approve'], ['correction', ClockIcon, 'Request correction'], ['reject', XCircleIcon, 'Reject']] as const).map(([value, Icon, label]) => <button key={value} onClick={() => setReviewDecision(value)} className={`flex items-center justify-center gap-2 rounded-md border px-3 py-2.5 text-sm font-bold ${reviewDecision === value ? 'border-emerald-700 bg-emerald-50 text-emerald-800' : 'border-slate-300 text-slate-700 hover:bg-slate-50'}`}><Icon className="h-5 w-5" />{label}</button>)}
                  </div>
                  <textarea className={`${inputClass} mt-3`} rows={3} value={reviewComment} onChange={(event) => setReviewComment(event.target.value)} placeholder="Required manager review comment" />
                  <button disabled={saving} onClick={completeReview} className="mt-3 rounded-md bg-slate-900 px-5 py-2.5 text-sm font-bold text-white disabled:opacity-50">Confirm review decision</button>
                </section>
              )}

              {isManager && ['approved', 'rejected'].includes(inspection.status) && (
                <section className="border-t border-slate-200 pt-5"><h3 className="text-sm font-bold uppercase text-slate-500">Follow-up work</h3><div className="mt-3 flex flex-col gap-3 sm:flex-row"><input type="date" className={inputClass} value={followUpDate} onChange={(event) => setFollowUpDate(event.target.value)} /><button disabled={saving} onClick={createFollowUp} className="shrink-0 rounded-md bg-amber-600 px-4 py-2 text-sm font-bold text-white disabled:opacity-50">Create follow-up inspection</button></div></section>
              )}

              {inspection.targets && inspection.targets.length > 0 && (
                <section><h3 className="text-sm font-bold uppercase text-slate-500">Inspection targets</h3><div className="mt-3 divide-y divide-slate-200 border-y border-slate-200">{inspection.targets.map((target) => <div key={target.id} className="flex items-center justify-between py-3 text-sm"><span className="font-semibold text-slate-900">{target.tree_code || `Tree #${target.tree_id}`}</span><span className="text-slate-500">{humanize(target.target_status)} · {humanize(target.health_status)}</span></div>)}</div></section>
              )}

              {audit.length > 0 && (
                <section><h3 className="text-sm font-bold uppercase text-slate-500">Audit history</h3><div className="mt-3 border-l-2 border-slate-200 pl-4">{audit.map((entry) => <div key={entry.id} className="relative pb-4"><span className="absolute -left-[21px] top-1 h-2.5 w-2.5 rounded-full bg-emerald-700" /><p className="text-sm font-semibold text-slate-900">{humanize(entry.action)}</p><p className="text-xs text-slate-500">{entry.actor_name || 'System'} · {formatDate(entry.created_at)}</p>{entry.comment && <p className="mt-1 text-sm text-slate-600">{entry.comment}</p>}</div>)}</div></section>
              )}

              {error && <div className="flex items-start gap-2 border border-red-200 bg-red-50 p-3 text-sm font-semibold text-red-700"><ExclamationTriangleIcon className="h-5 w-5 shrink-0" />{error}</div>}
            </div>
          )}
        </div>
      </aside>
    </div>
  );
};

export default InspectionReviewDrawer;
